import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/LoadingScreen';
import supabase from '../utils/supabaseClient';

const DashboardPage = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [userProfiles, setUserProfiles] = useState({
    startup: { exists: true, is_active: true }, // Startup profile is implicit for all users
    investor: { exists: false, is_active: false, data: null },
    service: { exists: false, is_active: false, data: null },
    pharmaceutical: { exists: false, is_active: false, data: null }
  });
  const [subscription, setSubscription] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    // Fetch real products from Supabase
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get products for the current user
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (productsError) throw productsError;
        setProducts(productsData || []);
        
        // Fetch user types data
        const { data: userTypesData, error: userTypesError } = await supabase
          .from('user_types')
          .select('*')
          .eq('user_id', user.id);
          
        if (userTypesError) throw userTypesError;
        
        // Create a map of user types
        const userTypesMap = {};
        userTypesData.forEach(type => {
          userTypesMap[type.type] = {
            is_active: type.is_active,
            is_primary: type.is_primary
          };
        });
        
        // Fetch user profiles
        const { data: investorData, error: investorError } = await supabase
          .from('investor_profiles')
          .select('*')
          .eq('user_id', user.id);
        
        const { data: serviceData, error: serviceError } = await supabase
          .from('service_provider_profiles')
          .select('*')
          .eq('user_id', user.id);
        
        const { data: pharmaData, error: pharmaError } = await supabase
          .from('pharmaceutical_profiles')
          .select('*')
          .eq('user_id', user.id);
        
        // Handle any serious errors (connection issues, etc.)
        if (
          (investorError && investorError.code !== 'PGRST116') || 
          (serviceError && serviceError.code !== 'PGRST116') || 
          (pharmaError && pharmaError.code !== 'PGRST116')
        ) {
          console.error('Serious error fetching profiles:', investorError || serviceError || pharmaError);
          throw investorError || serviceError || pharmaError;
        }
        
        // Fetch subscription data
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);
          
        if (!subscriptionError && subscriptionData && subscriptionData.length > 0) {
          setSubscription(subscriptionData[0]);
        }
        
        // Set user profiles with active status from user_types table
        setUserProfiles({
          startup: { 
            exists: true, 
            // For startups, consider active unless explicitly deactivated
            is_active: !userTypesMap['startup'] || userTypesMap['startup']?.is_active !== false,
            is_primary: userTypesMap['startup']?.is_primary || false
          },
          investor: { 
            exists: investorData && investorData.length > 0, 
            is_active: userTypesMap['investor']?.is_active || false,
            is_primary: userTypesMap['investor']?.is_primary || false,
            data: investorData && investorData.length > 0 ? investorData[0] : null
          },
          service: { 
            exists: serviceData && serviceData.length > 0, 
            is_active: userTypesMap['service_provider']?.is_active || false,
            is_primary: userTypesMap['service_provider']?.is_primary || false,
            data: serviceData && serviceData.length > 0 ? serviceData[0] : null
          },
          pharmaceutical: { 
            exists: pharmaData && pharmaData.length > 0, 
            is_active: userTypesMap['pharmaceutical']?.is_active || false,
            is_primary: userTypesMap['pharmaceutical']?.is_primary || false,
            data: pharmaData && pharmaData.length > 0 ? pharmaData[0] : null
          }
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Handle product deletion
  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setProductToDelete(null);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    
    try {
      setDeleteLoading(true);
      
      // Delete the product from Supabase
      const { error: deleteError } = await supabase
        .from('products')
        .delete()
        .eq('id', productToDelete.id)
        .eq('user_id', user.id); // Ensure user can only delete their own products
      
      if (deleteError) throw deleteError;
      
      // Update local state to remove the deleted product
      setProducts(products.filter(p => p.id !== productToDelete.id));
      setShowDeleteConfirm(false);
      setProductToDelete(null);
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product. Please try again later.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Function to toggle profile activation status
  const handleToggleProfile = async (type) => {
    // Map frontend profile types to database types
    const typeMap = {
      investor: 'investor',
      service: 'service_provider',
      pharmaceutical: 'pharmaceutical',
      startup: 'startup'
    };
    
    const dbType = typeMap[type];
    if (!dbType) return;
    
    setStatusUpdating(true);
    
    try {
      // Update the is_active status in the user_types table
      const { error } = await supabase
        .from('user_types')
        .upsert({ 
          user_id: user.id,
          type: dbType,
          is_active: !userProfiles[type].is_active,
          updated_at: new Date()
        }, {
          onConflict: 'user_id,type',
          ignoreDuplicates: false
        });
        
      if (error) throw error;
      
      // Update local state
      setUserProfiles({
        ...userProfiles,
        [type]: {
          ...userProfiles[type],
          is_active: !userProfiles[type].is_active
        }
      });
      
    } catch (err) {
      console.error(`Error toggling ${type} profile:`, err);
      setError(`Failed to update ${type} profile status. Please try again.`);
    } finally {
      setStatusUpdating(false);
    }
  };

  // Determine subscription status text
  const getSubscriptionStatus = () => {
    if (!subscription) return 'Free Trial';
    
    if (subscription.status === 'active') {
      return 'Premium (Active)';
    } else if (subscription.status === 'canceled') {
      return 'Premium (Canceled)';
    } else {
      return subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading your dashboard..." />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Welcome back, {user?.profile?.name && !user?.profile?.name.includes('@') ? user.profile.name : 'Anonymous'}!
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-md" role="alert">
          {error}
        </div>
      )}

      {/* Profile Cards Grid */}
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Profiles</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Startup Profile Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-100 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Startup Profile</h3>
                <p className="text-sm text-gray-600">Manage your products</p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                Products: <span className="font-semibold">{products.length}</span>
              </p>
              
              {/* Toggle switch for startup profile */}
              <div className="mt-2 flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userProfiles.startup.is_active}
                    onChange={() => handleToggleProfile('startup')}
                    className="sr-only peer"
                    disabled={statusUpdating}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    {userProfiles.startup.is_active ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>
              
              <div className="pt-2">
                <Link to="/profile/startup/edit" className="btn-primary text-sm px-3 py-1 mt-2 inline-flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Edit Profile
                </Link>
              </div>
              
              <div className="pt-1">
                <Link to="/products/create" className="btn-primary text-sm px-3 py-1 mt-2 inline-flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Product
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Investor Profile Card */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-sm border border-green-100 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-100 text-green-600 mr-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Investor Profile</h3>
                <p className="text-sm text-gray-600">
                  {userProfiles.investor.exists ? 'Manage your investor profile' : 'Create your investor profile'}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                Status: <span className="font-semibold">{userProfiles.investor.exists ? 'Created' : 'Not Created'}</span>
              </p>
              
              {/* Toggle switch for investor profile */}
              {userProfiles.investor.exists && (
                <div className="mt-2 flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userProfiles.investor.is_active}
                      onChange={() => handleToggleProfile('investor')}
                      className="sr-only peer"
                      disabled={statusUpdating}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      {userProfiles.investor.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </label>
                </div>
              )}
              
              <div className="pt-2">
                {userProfiles.investor.exists ? (
                  <Link to="/profile/investor/edit" className="btn-primary text-sm px-3 py-1 mt-2 inline-flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Edit Profile
                  </Link>
                ) : (
                  <Link to="/profile/investor/create" className="btn-primary text-sm px-3 py-1 mt-2 inline-flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Profile
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Service Provider Profile Card */}
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg shadow-sm border border-purple-100 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 mr-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Service Provider</h3>
                <p className="text-sm text-gray-600">
                  {userProfiles.service.exists ? 'Manage your service profile' : 'Create your service profile'}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                Status: <span className="font-semibold">{userProfiles.service.exists ? 'Created' : 'Not Created'}</span>
              </p>
              
              {/* Toggle switch for service provider profile */}
              {userProfiles.service.exists && (
                <div className="mt-2 flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userProfiles.service.is_active}
                      onChange={() => handleToggleProfile('service')}
                      className="sr-only peer"
                      disabled={statusUpdating}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      {userProfiles.service.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </label>
                </div>
              )}
              
              <div className="pt-2">
                {userProfiles.service.exists ? (
                  <Link to="/profile/service/edit" className="btn-primary text-sm px-3 py-1 mt-2 inline-flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Edit Profile
                  </Link>
                ) : (
                  <Link to="/profile/service/create" className="btn-primary text-sm px-3 py-1 mt-2 inline-flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Profile
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pharmaceutical Profile Card */}
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg shadow-sm border border-red-100 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 text-red-600 mr-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Pharmaceutical</h3>
                <p className="text-sm text-gray-600">
                  {userProfiles.pharmaceutical.exists ? 'Manage your pharmaceutical profile' : 'Create your pharmaceutical profile'}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                Status: <span className="font-semibold">{userProfiles.pharmaceutical.exists ? 'Created' : 'Not Created'}</span>
              </p>
              
              {/* Toggle switch for pharmaceutical profile */}
              {userProfiles.pharmaceutical.exists && (
                <div className="mt-2 flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userProfiles.pharmaceutical.is_active}
                      onChange={() => handleToggleProfile('pharmaceutical')}
                      className="sr-only peer"
                      disabled={statusUpdating}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      {userProfiles.pharmaceutical.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </label>
                </div>
              )}
              
              <div className="pt-2">
                {userProfiles.pharmaceutical.exists ? (
                  <Link to="/profile/pharmaceutical/edit" className="btn-primary text-sm px-3 py-1 mt-2 inline-flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Edit Profile
                  </Link>
                ) : (
                  <Link to="/profile/pharmaceutical/create" className="btn-primary text-sm px-3 py-1 mt-2 inline-flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Profile
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Section (Only for Startup profile) */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Products</h2>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          {products.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-500 mb-4">You haven't created any products yet.</p>
              <Link to="/products/create" className="btn-primary-outline">
                Create Your First Product
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stage
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {product.development_stage}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.product_category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(product.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/products/${product.id}`} className="text-primary-600 hover:text-primary-900 mr-4">
                          View
                        </Link>
                        <Link to={`/products/${product.id}/input`} className="text-green-600 hover:text-green-900 mr-4">
                          Edit
                        </Link>
                        <button 
                          onClick={() => handleDeleteClick(product)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Messages and Subscription Row */}
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Status</h2>
          <div className="p-4 bg-gray-50 rounded-md">
            <p className="font-medium text-gray-700">Current Plan: <span className="text-primary-600">{getSubscriptionStatus()}</span></p>
            <p className="text-sm text-gray-500 mt-2">Manage your subscription to access more features.</p>
            <div className="mt-4 flex space-x-4">
              {/* Disabled Manage Subscription button - to be enabled later */}
              <button 
                className="btn-secondary block text-center flex-grow cursor-not-allowed opacity-60" 
                disabled
                title="This feature will be available soon"
              >
                Subscription Coming Soon
              </button>
              <Link to="/messaging" className="btn-primary block text-center flex-grow">
                Go to Messages
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete "{productToDelete?.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="btn-secondary"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn-danger"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage; 